import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip
} from "recharts";
import styles from '../assets/styles/ESGPortfolioDashboard.module.css';

const pillarTable = (
  <div className={styles.card} style={{ marginTop: 24 }}>
    <div className={styles.cardBody}>
      <h3>ESG Pillar Framework</h3>
      <table className={styles.table} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>        
        <thead>
          <tr>
            <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>Pillar Code</th>
            <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>Title</th>
            <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>Description</th>
            <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>Typical Indicators</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>E1</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Nutrient Efficiency</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Measures how effectively nitrogen (N), phosphorus (P), and potassium (K) inputs are utilised relative to outputs.</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Nutrient inflow : outflow balance, N-retention %, nutrient circularity, soil test compliance, ammonia loss.</td>
          </tr>
          <tr>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>E2</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>GHG Intensity</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Quantifies greenhouse-gas emissions per unit of livestock or crop output.</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>CH₄, N₂O, CO₂e intensity, soil carbon change (ΔSOC t C ha⁻¹ yr⁻¹), renewable energy share.</td>
          </tr>
          <tr>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>E3</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Manure Management</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Evaluates nutrient capture and recycling from manures, slurries, and digestates.</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Storage integrity, ammonia recovery, bio-ARS integration, digestate application practice, runoff risk.</td>
          </tr>
          <tr>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>S4</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Land & Soil (Biodiversity & Social)</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Represents the combined social-environmental value of land stewardship and biodiversity provision.</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>GS4 / SW7 / SW14 habitat share %, SOM %, pH balance, hedgerow %, pollinator cover, local employment and training.</td>
          </tr>
          <tr>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>G5</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Data Transparency (Governance)</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Captures evidence, traceability, and compliance readiness across the supply chain.</td>
            <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Record-keeping, NMP verification, carbon/NMRV data access, audit readiness, certification status.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

const ESGPortfolioDashboard = () => {
  const navigate = useNavigate();
  const [selectedFarm, setSelectedFarm] = useState(null);

  const farms = [
    { id: "X10AI_FT_040", tier: "Regenerative Leader", index: 89, color: "#78BE20", notes: "Near-zero N balance; verified transparency; lowest GHG intensity.", rationale: "Regenerative benchmark with precision nutrient management and verified data integrity." },
    { id: "X10AI_FT_020", tier: "Circular Performer", index: 88, color: "#00857C", notes: "Integrated arable-livestock system with full FYM tracking.", rationale: "Low emissions intensity through mixed-farm circularity." },
    { id: "X10AI_FT_050", tier: "Balanced Performer", index: 84, color: "#F5A623", notes: "Strong manure capture, measurable soil carbon gains.", rationale: "Scalable ESG improvement and circular nutrient flow." },
    { id: "X10AI_FT_010", tier: "Compliance Stage", index: 78, color: "#00563F", notes: "Verified soil data, pH 6.5, OM 6.4%, NVZ compliant.", rationale: "Solid compliance; needs better fertiliser and energy monitoring." },
    { id: "X10AI_FT_060", tier: "Baseline", index: 67, color: "#666666", notes: "Modelled Tier-1 defaults, accurate livestock base.", rationale: "Baseline comparator for measured data." },
    { id: "X10AI_FT_030", tier: "Improvement Required", index: 54, color: "#C62828", notes: "High nutrient imports (~46t N inflow).", rationale: "Target nutrient recovery and circular-improvement intervention." }
  ];

  const radarData = [
    { pillar: "E1 N Efficiency", X10AI_FT_040: 91, X10AI_FT_020: 88, X10AI_FT_050: 86, X10AI_FT_010: 80, X10AI_FT_060: 65, X10AI_FT_030: 58 },
    { pillar: "E2 GHG Intensity", X10AI_FT_040: 88, X10AI_FT_020: 91, X10AI_FT_050: 85, X10AI_FT_010: 72, X10AI_FT_060: 70, X10AI_FT_030: 60 },
    { pillar: "E3 Manure Mgmt",  X10AI_FT_040: 87, X10AI_FT_020: 90, X10AI_FT_050: 91, X10AI_FT_010: 77, X10AI_FT_060: 68, X10AI_FT_030: 56 },
    { pillar: "S4 Land & Soils", X10AI_FT_040: 89, X10AI_FT_020: 84, X10AI_FT_050: 84, X10AI_FT_010: 92, X10AI_FT_060: 66, X10AI_FT_030: 64 },
    { pillar: "G5 Transparency", X10AI_FT_040: 90, X10AI_FT_020: 87, X10AI_FT_050: 76, X10AI_FT_010: 70, X10AI_FT_060: 66, X10AI_FT_030: 32 }
  ];

  return (
    <div className={styles.container}>
      <div className={styles.h1}>FarmTwin ESG Benchmark (2024–2025)</div>
      <div className={styles.subtle}>ABP/Sainsbury’s Commercial Pilot – x10AI ESG Framework (E1–E5, weighted 20% each)</div>
      {pillarTable}
      {/* Farm summary cards */}
      <div className={`${styles.grid} ${styles.row3}`} style={{ marginTop: 16 }}>
        {farms.map(farm => (
          <motion.div key={farm.id} whileHover={{ scale: 1.02 }} 
            onClick={() => {
              if(farm.id === "X10AI_FT_050") {
                navigate("/neilharleyesg");
              } else {
                setSelectedFarm(farm);
              }
            }}
          >
            <div className={styles.card} style={{ cursor: "pointer" }}>
              <div className={styles.cardBody}>
                <h2>{farm.id}</h2>
                <div className={styles.subtle} style={{ marginBottom: 8 }}>{farm.tier}</div>
                <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>{farm.index}</div>
                <div className={styles.progress}>
                  <span style={{ width: `${farm.index}%`, background: farm.color }}></span>
                </div>
                <div className={styles.subtle} style={{ fontSize: 12, marginTop: 6 }}>Click to view details</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Farm Details */}
      {selectedFarm && (
        <div className={`${styles.card} ${styles.details}`} style={{ marginTop: 24, borderTopColor: selectedFarm.color }}>
          <div className={styles.cardBody}>
            <h3>{selectedFarm.id} – {selectedFarm.tier}</h3>
            <p><b>ESG Index:</b> {selectedFarm.index}/100</p>
            <p><b>Notes:</b> {selectedFarm.notes}</p>
            <p><b>Rationale:</b> {selectedFarm.rationale}</p>
            <button className={styles.buttonLink} onClick={() => setSelectedFarm(null)}>Close panel</button>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className={`${styles.grid} ${styles.row2}`} style={{ marginTop: 16 }}>
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <h3>ESG Pillar Comparison</h3>
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="pillar" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  {farms.map(farm => (
                    <Radar key={farm.id} name={farm.id} dataKey={farm.id} stroke={farm.color} fill={farm.color} fillOpacity={0.2} />
                  ))}
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardBody}>
            <h3>ESG Index Leaderboard</h3>
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[...farms].sort((a,b)=>b.index-a.index)} layout="vertical" margin={{ left: 50 }}>
                  <XAxis type="number" domain={[0,100]} hide />
                  <YAxis dataKey="id" type="category" width={120} />
                  <Tooltip />
                  <Bar dataKey="index" radius={[6,6,6,6]} fill="#00563F" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Insights & Performance Spectrum */}
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <h3>Key Insights</h3>
            <ul>
              <li><b>Leader:</b> X10AI_FT_040 — near-closed nutrient loop; verified records; low CH₄/N₂O.</li>
              <li><b>Mixed-system efficiency:</b> X10AI_FT_020 — low GHG intensity with FYM recycling.</li>
              <li><b>Balanced circularity:</b> X10AI_FT_050 — strong manure capture; measurable soil C gains.</li>
              <li><b>Compliance strength:</b> X10AI_FT_010 — solid soils/NVZ; needs fuller input/energy audits.</li>
              <li><b>Baseline model:</b> X10AI_FT_060 — Tier‑1 defaults; comparator for measured data.</li>
              <li><b>Risk flag:</b> X10AI_FT_030 — high nutrient imports; target circular recovery.</li>
            </ul>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardBody}>
            <h3>Performance Spectrum</h3>
            <div className={styles.subtle} style={{ marginBottom: 12 }}>Positioned by ESG Index (0–100)</div>
            <div style={{ position: "relative", width: "100%", height: 12, borderRadius: 999, background: "linear-gradient(90deg, #78BE20, #F5A623, #C62828)" }} />
            <div style={{ position: "relative", marginTop: 24, height: 48 }}>
              {[...farms].sort((a,b)=>a.index-b.index).map((farm) => (
                <div key={farm.id} className={styles.marker} style={{ left: `${farm.index}%` }}>
                  <div className={`${styles.flexCol} ${styles.itemsCenter}`}>
                    <div style={{ padding: "2px 8px", fontSize: 12, borderRadius: 999, background: "#fff", border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}>{farm.id}</div>
                    <div className={styles.markerStub} />
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.legend}>
              <span>Baseline</span><span>Compliance Stage</span><span>Circular Performer</span><span>Regenerative Leader</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ESGPortfolioDashboard;
